import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, TrendUp, TrendDown, Sliders, CurrencyDollar } from "@phosphor-icons/react";

const REASONS = {
  in: ["purchase", "production", "return", "transfer", "adjustment", "other"],
  out: ["sale", "production", "damage", "transfer", "adjustment", "other"],
  adjustment: ["adjustment", "damage", "other"],
};

export default function StockDialog({ open, onClose, part, onSaved }) {
  const [type, setType] = useState("in");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("purchase");
  const [note, setNote] = useState("");
  const [unitCost, setUnitCost] = useState(0);
  const [updatePartCost, setUpdatePartCost] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && part) {
      setUnitCost(Number(part.unit_cost || 0));
      setUpdatePartCost(false);
    }
  }, [open, part]);

  if (!open || !part) return null;

  // initialization happens via effect above
  void unitCost;

  const setTxnType = (t) => {
    setType(t);
    setReason(REASONS[t][0]);
    if (t === "adjustment") setQuantity(part.stock_qty);
    if (t !== "in") setUpdatePartCost(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        part_id: part.id,
        type,
        quantity: Number(quantity),
        reason,
        note,
        unit_cost: Number(unitCost),
      };
      if (type === "in") payload.update_part_cost = updatePartCost;
      await api.post("/transactions", payload);
      toast.success("Stock updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const projected =
    type === "in"
      ? part.stock_qty + Number(quantity || 0)
      : type === "out"
      ? part.stock_qty - Number(quantity || 0)
      : Number(quantity || 0);

  return (
    <div className="fixed inset-0 z-50 bg-[#1A2332]/60 flex items-start sm:items-center justify-center pt-4 sm:pt-4 px-0 sm:px-4 pb-0 sm:pb-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-zinc-300 w-full max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="stock-dialog"
      >
        <div className="flex items-center justify-between border-b border-zinc-300 px-4 sm:px-6 py-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">
              Stock movement
            </div>
            <h2 className="font-display text-xl font-semibold">Adjust stock</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100" data-testid="stock-dialog-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="px-4 sm:px-6 py-5 space-y-4">
          {/* Part summary */}
          <div className="bg-zinc-50 border border-zinc-200 px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{part.sku}</div>
            <div className="font-medium text-sm mt-0.5">{part.name}</div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Current stock</span>
              <span className="font-mono font-bold text-2xl">{part.stock_qty}</span>
            </div>
          </div>

          {/* Type buttons */}
          <div className="grid grid-cols-3 gap-px bg-zinc-300">
            {[
              { v: "in", label: "Stock IN", icon: TrendUp, color: "bg-emerald-600 text-white" },
              { v: "out", label: "Stock OUT", icon: TrendDown, color: "bg-red-600 text-white" },
              { v: "adjustment", label: "Adjust", icon: Sliders, color: "bg-amber-400 text-amber-950" },
            ].map(({ v, label, icon: Icon, color }) => (
              <button
                key={v}
                type="button"
                onClick={() => setTxnType(v)}
                data-testid={`stock-type-${v}`}
                className={`flex items-center justify-center gap-1.5 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                  type === v ? color : "bg-white hover:bg-zinc-100 text-zinc-700"
                }`}
              >
                <Icon size={14} weight="bold" /> {label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
              {type === "adjustment" ? "Set stock to" : "Quantity"}
            </label>
            <input
              type="number"
              min={type === "adjustment" ? 0 : 1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              data-testid="stock-quantity-input"
              className="mt-1.5 w-full px-4 py-3 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-lg font-bold"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="stock-reason-select"
              className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] capitalize"
            >
              {REASONS[type].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
              <span className="flex items-center gap-1">
                <CurrencyDollar size={12} weight="bold" /> Unit cost
              </span>
              <span className="text-zinc-400 normal-case tracking-normal">
                Part default: ${Number(part.unit_cost || 0).toFixed(2)}
              </span>
            </label>
            <div className="mt-1.5 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                data-testid="stock-unit-cost-input"
                className="w-full pl-7 pr-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono"
              />
            </div>
            {type === "in" && Number(unitCost) !== Number(part.unit_cost || 0) && (
              <label className="mt-2 flex items-center gap-2 text-xs text-zinc-700">
                <input
                  type="checkbox"
                  checked={updatePartCost}
                  onChange={(e) => setUpdatePartCost(e.target.checked)}
                  data-testid="stock-update-part-cost"
                  className="h-4 w-4"
                />
                Update part&apos;s cost going forward to ${Number(unitCost).toFixed(2)}
              </label>
            )}
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="stock-note-input"
              className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] min-h-[60px]"
              placeholder="Reference, PO number, etc."
            />
          </div>

          <div className="bg-[#1A2332] text-zinc-100 px-4 py-3 grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">Projected stock</span>
              <div
                className={`font-mono font-bold text-2xl ${projected < 0 ? "text-red-500" : "text-emerald-400"}`}
                data-testid="stock-projected"
              >
                {projected}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">
                {type === "adjustment" ? "Adjustment value" : type === "in" ? "Cost in" : "Value out"}
              </span>
              <div className="font-mono font-bold text-2xl text-[#FF9800]" data-testid="stock-projected-cost">
                ${(Number(unitCost) * Math.max(Number(quantity || 0) - (type === "adjustment" ? part.stock_qty : 0), Number(quantity || 0))).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100"
              data-testid="stock-dialog-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || projected < 0}
              data-testid="stock-dialog-save"
              className="px-5 py-2 text-sm font-semibold bg-[#FF9800] hover:bg-[#E68900] text-white disabled:opacity-60"
            >
              {saving ? "Saving…" : "Confirm movement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
